// Extend the AuthContext from the Supabase context to maintain compatibility
import { AuthContext as SupabaseAuthContext, AuthContextProvider } from '@/context/SupabaseAuthContext';
import { createContext, useContext } from 'react';

// Define the User type to match what the Form component expects
export interface User {
  id: string;
  email?: string;
  credits?: number;
  created_at?: string;
  // Add other user properties as needed
}

// Create a wrapper context that transforms Supabase user to our User type
interface ExtendedAuthContextType {
  user: User | null;
  session?: any;
  isVerified: boolean;
  verificationChecked: boolean;
  isLoading: boolean;
  isProfileLoading: boolean;
  refreshProfile: () => Promise<void>;
}

// Create a new context that properly maps the Supabase user properties
export const AuthContext = createContext<ExtendedAuthContextType>({
  user: null,
  isVerified: false,
  verificationChecked: false,
  isLoading: true,
  isProfileLoading: false,
  refreshProfile: async () => { }
});

// Custom hook that transforms the Supabase user to our User type
export const useAuthContext = () => {
  // Get the Supabase auth context values
  const supabaseAuth = useContext(SupabaseAuthContext);

  // Transform the Supabase user to our User type if it exists
  const transformedUser: User | null = supabaseAuth.user ? {
    id: supabaseAuth.user.id,
    email: supabaseAuth.user.email,
    credits: supabaseAuth.profile?.credits ?? 0,
    created_at: supabaseAuth.user.created_at
  } : null;

  // Return the transformed context
  return {
    user: transformedUser,
    session: supabaseAuth.session,
    isVerified: supabaseAuth.isVerified,
    verificationChecked: supabaseAuth.verificationChecked,
    isLoading: supabaseAuth.isLoading,
    isProfileLoading: supabaseAuth.isProfileLoading,
    refreshProfile: supabaseAuth.refreshProfile
  };
};

// Re-export the AuthContextProvider for convenience
export { AuthContextProvider };
